import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Radio } from '@components/core-ui/form-elements/Radio';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { Tooltip } from '@components/core-ui/Tooltip';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { JobsAdminService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { adminWorkspacesKeys } from 'app/queries/adminWorkspaces/adminWorkspacesKeys';
import { createFetchAllOrganizationsQueryFn } from 'app/queries/organizations/createFetchAllOrganizationsQueryFn';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { useState } from 'react';

import { createFetchAllAdminWorkspacesQueryFn } from './MoveJobToOrgWsModal/api/createFetchAllAdminWorkspacesQueryFn';
import { Content } from './MoveJobToOrgWsModal/styles/Content';

type TProps = {
  jobId: string;
  workspaceId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AdminMoveJobToOrgWsModal = ({ jobId, workspaceId, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(workspaceId);

  const organizationsQuery = useQuery({
    queryKey: organizationsKeys.allList(),
    queryFn: createFetchAllOrganizationsQueryFn(call),
  });

  const workspacesQuery = useQuery({
    queryKey: adminWorkspacesKeys.allList(),
    queryFn: createFetchAllAdminWorkspacesQueryFn(call),
  });
  useNotifyQueryError(organizationsQuery.error);
  useNotifyQueryError(workspacesQuery.error);

  const allOrganizations = (organizationsQuery.data ?? []).map((org) => ({
    value: org.id,
    label: `${org.name} (${org.slug})`,
  }));
  const workspaces = (workspacesQuery.data ?? []).filter((ws) => ws.organizationId === selectedOrganizationId);

  const moveMutation = useMutation({
    mutationFn: () =>
      call(() =>
        JobsAdminService.adminMoveJobToOrgWs({ jobId, requestBody: { workspaceId: selectedWorkspaceId ?? '' } }),
      ),
    onSuccess: () => {
      onSuccess();
      onCancel();
    },
    onError: notifyApiCallError,
  });

  const handleSubmit = () => moveMutation.mutate();

  return (
    <ModalWrapper width="487px">
      {moveMutation.isPending ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Move Job to another workspace</ModalTitle>
      <Content>
        <AutoCompleteSelect
          name="organizationId"
          label="Organization"
          className="form-autocomplete"
          value={selectedOrganizationId}
          onChange={(_, value) => setSelectedOrganizationId(value)}
          options={allOrganizations}
          required={false}
        />

        <TableWrapper className="customTable">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Workspace Name</Th>
              </Tr>
            </Thead>
            <Tbody>
              {workspaces.map((data) => {
                const disabled = workspaceId === data.id;
                return (
                  <Tr key={data.id}>
                    <Td>
                      {disabled ? (
                        <Tooltip content="Cannot move the job to the same Workspace" placement="bottom">
                          <div className="radioWrapper disabled">
                            <Radio onChange={() => {}} checked={selectedWorkspaceId === data.id} disabled={true}>
                              {data.name} ({data.slug})
                            </Radio>
                          </div>
                        </Tooltip>
                      ) : (
                        <div className="radioWrapper">
                          <Radio
                            onChange={() => setSelectedWorkspaceId(data.id)}
                            checked={selectedWorkspaceId === data.id}
                          >
                            {data.name} ({data.slug})
                          </Radio>
                        </div>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>

        {organizationsQuery.isError || workspacesQuery.isError ? (
          <ErrorMessage error="Failed to load the organizations and workspaces. Please try again." />
        ) : null}

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={moveMutation.isPending || selectedWorkspaceId === workspaceId} onClick={handleSubmit}>
            Move
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
